import type { Theme } from "@mui/material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useUpdateKpi } from "../../hooks/mutation/update/useUpdateKpi";
import { useKpi } from "../../hooks/query/useKpi";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().optional().default(""),
  warning: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
  critical: z.number().min(0, "Minimum is 0").max(100, "Maximum is 100"),
});

type FormValues = z.input<typeof schema>;

const EditKpiDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  kpiId: string;
}> = ({ onDialogClosed, kpiId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isFormReady, setIsFormReady] = React.useState(false);

  const kpiQuery = useKpi(kpiId);
  const isSystemKpi = kpiQuery.data?.system === true;
  const { mutateAsync: updateKpi, isPending: isUpdating } = useUpdateKpi();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      warning: 50,
      critical: 50,
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    setIsFormReady(false);
  }, [kpiId]);

  React.useEffect(() => {
    const kpi = kpiQuery.data;
    if (!kpi) {
      return;
    }

    reset({
      name: kpi.name,
      description: kpi.description ?? "",
      warning: kpi.thresholds.warning,
      critical: kpi.thresholds.critical,
    });
    setIsFormReady(true);
  }, [kpiQuery.data, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      if (isSystemKpi) {
        setSubmitError("System KPIs cannot be edited.");
        return;
      }
      setSubmitError(null);
      await updateKpi({
        kpiId,
        kpi: {
          name: data.name.trim(),
          description: data.description?.trim() ?? "",
          thresholds: {
            warning: data.warning,
            critical: data.critical,
          },
        },
      });

      onDialogClosed({ message: "KPI updated!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isUpdating) {
      return;
    }
    setSubmitError(null);
    onDialogClosed();
  };

  return (
    <Dialog
      open
      key={kpiId}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title="Edit KPI"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box component="form" id="edit-kpi-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Update KPI metadata and thresholds.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            {kpiQuery.isError && (
              <FormHelperText error sx={{ fontSize: 13, m: 0, width: "100%" }}>
                {kpiQuery.error instanceof Error
                  ? kpiQuery.error.message
                  : "Could not load KPI."}
              </FormHelperText>
            )}

            {!kpiQuery.isError && !isFormReady && (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={22} />
              </Box>
            )}

            <Stack
              spacing={2.25}
              sx={{ width: "100%", display: isFormReady ? "flex" : "none" }}
            >
              {isSystemKpi && (
                <FormHelperText sx={{ fontSize: 13, m: 0 }}>
                  System KPIs are read-only.
                </FormHelperText>
              )}

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Error Rate"
                  disabled={isSystemKpi}
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the KPI"
                  disabled={isSystemKpi}
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Warning threshold</FormLabel>
                  <TextField
                    size="small"
                    type="number"
                    disabled={isSystemKpi}
                    error={!!errors.warning}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("warning", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.warning}>
                    {errors.warning?.message ?? " "}
                  </FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Critical threshold</FormLabel>
                  <TextField
                    size="small"
                    type="number"
                    disabled={isSystemKpi}
                    error={!!errors.critical}
                    inputProps={{ min: 0, max: 100 }}
                    {...register("critical", {
                      valueAsNumber: true,
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.critical}>
                    {errors.critical?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              </Stack>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Save"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabledReason={isSystemKpi ? "System KPIs are read-only." : undefined}
          disabled={!kpiQuery.data || isUpdating || isSystemKpi}
        />
      </Box>
    </Dialog>
  );
};

export default EditKpiDialog;
